import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, ShoppingBag, Users, Activity, LucideIcon } from 'lucide-react';
import { useDashboardCards } from '@/hooks/use-reports';

import { cn, formatCurrency } from '@/lib/utils';

import { Link } from '@tanstack/react-router';

interface StatsCardProps {
  title: string;
  value: string;
  description: string;
  icon: LucideIcon;
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
  href?: string;
}

export function StatsCard({ title, value, description, icon: Icon, className, href }: StatsCardProps) {
  const content = (
    <Card className={cn(
      "min-w-0 transition-all duration-200", 
      href && "hover:shadow-md hover:border-primary/50 cursor-pointer active:scale-[0.98] transition-transform",
      className
    )}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium truncate">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold truncate">{value}</div>
        <p className="text-xs text-muted-foreground truncate">{description}</p>
      </CardContent>
    </Card>
  );

  if (href) {
    return <Link to={href as any}>{content}</Link>;
  }

  return content;
}



import { useCustomerStore } from '@/store/customer-store';

/**
 * DashboardStats Component
 * 
 * Renders the top-level metric cards for the dashboard (Revenue, Orders, AOV, Customers).
 * Fetches data via the `useDashboardCards` React Query hook.
 * Includes robust skeleton loading states.
 */
export function DashboardStats() {
  const { data: cards, isLoading, error } = useDashboardCards(30);
  const { customers } = useCustomerStore();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="animate-pulse h-24" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="border-destructive/50 bg-destructive/5 shadow-sm">
             <CardContent className="pt-6 text-center">
                <p className="text-xs font-bold text-destructive uppercase tracking-tighter">Sync Failed</p>
                <p className="text-[10px] text-muted-foreground mt-1">Retry automatic</p>
             </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <StatsCard
        title="Total Revenue"
        value={formatCurrency(cards?.totalRevenue || 0)}
        description="Last 30 days revenue"
        icon={DollarSign}
        trend="up"
        href="/reports/profit-loss"
      />
      <StatsCard
        title="Recent Orders"
        value={cards?.totalOrders?.toString() || "0"}
        description="Last 30 days orders"
        icon={ShoppingBag}
        trend="up"
        href="/orders"
      />
      <StatsCard
        title="Avg. Order Value"
        value={formatCurrency(cards?.avgOrderValue || 0)}
        description="Last 30 days average"
        icon={Activity}
        trend="neutral"
        href="/reports/profit-loss"
      />
       <StatsCard
        title="Total Customers"
        value={customers.length.toString()}
        description="Registered customers"
        icon={Users}
        trend="up"
        href="/customers"
      />
    </div>
  );
}
